import { HttpErrorResponse } from "@angular/common/http";
import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  RowClickedEvent,
} from "ag-grid-community";
import { Subscription } from "rxjs";
import {
  CurrentQuoteDetail,
  CurrentQuoteListItem,
  QuoteAcceptance,
  QuoteCarrierCost,
  QuoteCarrierOption,
  QuoteConversionResponse,
  QuoteCustomerCharge,
  QuoteEvent,
  QuoteHistoricalMatch,
  QuoteLineItem,
  QuoteListQuery,
  QuoteRateObservation,
  QuoteSort,
  QuotesApiService,
} from "./quotes-api.service";

type ThemeMode = "dark" | "light";

interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

@Component({
  selector: "app-quotes",
  templateUrl: "./quotes.component.html",
  styleUrls: ["./quotes.component.scss"],
})
export class QuotesComponent implements OnInit, OnDestroy {
  readonly statusOptions = [
    "",
    "draft",
    "submitted",
    "accepted",
    "declined",
    "expired",
    "converted",
    "voided",
  ];
  readonly sourceOptions = [
    "",
    "customer_rate_quote",
    "create_quote",
    "freightmancer",
    "api",
  ];
  readonly modeOptions = ["", "quick", "detailed"];
  readonly customerStatusOptions = [
    "",
    "not_presented",
    "pending",
    "approved",
    "declined",
    "change_required",
  ];
  readonly assessmentStatusOptions = [
    "",
    "not_ready",
    "pending",
    "reviewing",
    "approved",
    "blocked",
  ];
  readonly capacityStatusOptions = [
    "",
    "unassigned",
    "sourcing",
    "selected",
    "confirmed",
    "unavailable",
  ];
  readonly priorityOptions = ["", "0", "1", "2", "3"];
  readonly sortOptions: SelectOption<QuoteSort>[] = [
    { value: "activity_desc", label: "Priority + recent activity" },
    { value: "activity_asc", label: "Oldest activity" },
    { value: "priority_desc", label: "Highest priority" },
    { value: "pickup_asc", label: "Pickup date: soonest" },
    { value: "pickup_desc", label: "Pickup date: latest" },
    { value: "margin_desc", label: "Margin: highest" },
    { value: "margin_asc", label: "Margin: lowest" },
    { value: "charge_desc", label: "Customer charge: highest" },
    { value: "cost_desc", label: "Carrier cost: highest" },
    { value: "created_desc", label: "Newest quote" },
    { value: "created_asc", label: "Oldest quote" },
    { value: "expiry_asc", label: "Expires soonest" },
    { value: "number_desc", label: "Quote number: newest" },
  ];
  readonly pageSizeOptions = [25, 50, 100, 200];
  readonly overlayLoadingTemplate =
    '<span class="qw-grid-overlay"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading quote queue</span>';
  readonly overlayNoRowsTemplate =
    '<span class="qw-grid-overlay">No quotes match this view.</span>';

  themeMode: ThemeMode = "dark";
  search = "";
  status = "";
  source = "";
  quoteMode = "";
  customerStatus = "";
  assessmentStatus = "";
  capacityStatus = "";
  priority = "";
  pickupFrom = "";
  pickupTo = "";
  onlyUnassigned = false;
  onlyBlockers = false;
  converted = "";
  sort: QuoteSort = "activity_desc";
  pageSize = 50;
  offset = 0;
  total = 0;
  hasMore = false;
  showAdvancedFilters = false;

  rows: CurrentQuoteListItem[] = [];
  loading = true;
  hasLoaded = false;
  errorMessage = "";
  selectedSummary: CurrentQuoteListItem | null = null;
  selectedDetail: CurrentQuoteDetail | null = null;
  detailLoading = false;
  detailError = "";
  pdfDownloading = false;
  pdfError = "";
  convertingQuoteUuid: string | null = null;
  conversionError = "";
  conversionResult: QuoteConversionResponse["data"] | null = null;

  readonly defaultColDef: ColDef<CurrentQuoteListItem> = {
    resizable: true,
    sortable: false,
    filter: false,
    minWidth: 110,
  };

  readonly gridOptions: GridOptions<CurrentQuoteListItem> = {
    rowHeight: 68,
    headerHeight: 44,
    animateRows: false,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    getRowId: (params) => params.data.quote_uuid,
  };

  readonly columnDefs: ColDef<CurrentQuoteListItem>[] = [
    {
      headerName: "Quote",
      field: "display_quote_number",
      minWidth: 175,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderQuoteCell(params.data),
    },
    {
      headerName: "Customer",
      field: "customer_display_name",
      minWidth: 210,
      flex: 1.05,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderCustomerCell(params.data),
    },
    {
      headerName: "Lane",
      colId: "lane",
      minWidth: 250,
      flex: 1.35,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderLaneCell(params.data),
    },
    {
      headerName: "Pickup / Freight",
      colId: "freight",
      minWidth: 190,
      flex: 0.9,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderFreightCell(params.data),
    },
    {
      headerName: "Customer Charge",
      colId: "charge",
      minWidth: 145,
      flex: 0.7,
      type: "rightAligned",
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderMoneyCell(
          params.data?.pricing.charge_total_cents,
          params.data?.pricing.currency || params.data?.currency,
          params.data?.pricing.charge_complete ? "Complete" : "Needs pricing",
        ),
    },
    {
      headerName: "Carrier Cost",
      colId: "cost",
      minWidth: 145,
      flex: 0.7,
      type: "rightAligned",
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderMoneyCell(
          params.data?.pricing.cost_total_cents,
          params.data?.pricing.cost_currency,
          params.data?.pricing.cost_complete ? "Complete" : "Needs cost",
        ),
    },
    {
      headerName: "Margin",
      colId: "margin",
      minWidth: 145,
      flex: 0.7,
      type: "rightAligned",
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderMarginCell(params.data),
    },
    {
      headerName: "Carrier",
      colId: "carrier",
      minWidth: 190,
      flex: 0.9,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderCarrierCell(params.data),
    },
    {
      headerName: "Readiness",
      colId: "readiness",
      minWidth: 220,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderReadinessCell(params.data),
    },
    {
      headerName: "Expiry",
      field: "expiry_date",
      minWidth: 145,
      flex: 0.68,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderExpiryCell(params.data),
    },
    {
      headerName: "Owner / Activity",
      colId: "activity",
      minWidth: 185,
      flex: 0.85,
      cellRenderer: (params: ICellRendererParams<CurrentQuoteListItem>) =>
        this.renderActivityCell(params.data),
    },
    {
      headerName: "",
      colId: "open",
      width: 54,
      minWidth: 54,
      maxWidth: 54,
      pinned: "right",
      cellRenderer: () =>
        '<span class="qw-grid-open" aria-hidden="true"><i class="fa-solid fa-chevron-right"></i></span>',
    },
  ];

  private gridApi: GridApi<CurrentQuoteListItem> | null = null;
  private listSubscription: Subscription | null = null;
  private detailSubscription: Subscription | null = null;
  private listRequestSequence = 0;
  private detailRequestSequence = 0;
  private readonly themeStorageKey = "quote-workbench-theme";
  private readonly terminalStatuses = new Set([
    "declined",
    "expired",
    "converted",
    "voided",
  ]);

  constructor(
    private readonly quotesApi: QuotesApiService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.themeMode = this.storedTheme();
    this.load(true);
  }

  ngOnDestroy(): void {
    this.listSubscription?.unsubscribe();
    this.detailSubscription?.unsubscribe();
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.selectedSummary || this.detailLoading) {
      this.closeDetail();
    }
  }

  get isDarkTheme(): boolean {
    return this.themeMode === "dark";
  }

  get gridThemeClass(): string {
    return this.isDarkTheme ? "ag-theme-ulaap-dark" : "ag-theme-ulaap";
  }

  get pageStart(): number {
    return this.total === 0 ? 0 : this.offset + 1;
  }

  get pageEnd(): number {
    return Math.min(this.total, this.offset + this.rows.length);
  }

  get canGoBack(): boolean {
    return this.offset > 0;
  }

  get pageNeedsAssessment(): number {
    return this.rows.filter(
      (quote) =>
        !this.terminalStatuses.has(quote.effective_status) &&
        quote.assessment_status !== "approved",
    ).length;
  }

  get pageNeedsCapacity(): number {
    return this.rows.filter(
      (quote) =>
        !this.terminalStatuses.has(quote.effective_status) &&
        !["selected", "confirmed"].includes(quote.capacity_status),
    ).length;
  }

  get pageReadyToConvert(): number {
    return this.rows.filter((quote) => quote.capabilities.can_convert_to_lading)
      .length;
  }

  get pageAverageMargin(): number | null {
    const margins = this.rows
      .filter(
        (quote) =>
          quote.pricing.margin_complete && !quote.pricing.currency_mismatch,
      )
      .filter((quote) => quote.pricing.margin_percent !== null)
      .map((quote) => Number(quote.pricing.margin_percent))
      .filter((margin) => Number.isFinite(margin));

    if (!margins.length) {
      return null;
    }
    return margins.reduce((sum, margin) => sum + margin, 0) / margins.length;
  }

  get hasActiveFilters(): boolean {
    return Boolean(
      this.search.trim() ||
        this.status ||
        this.source ||
        this.quoteMode ||
        this.customerStatus ||
        this.assessmentStatus ||
        this.capacityStatus ||
        this.priority ||
        this.pickupFrom ||
        this.pickupTo ||
        this.onlyUnassigned ||
        this.onlyBlockers ||
        this.converted,
    );
  }

  onGridReady(event: GridReadyEvent<CurrentQuoteListItem>): void {
    this.gridApi = event.api;
    this.syncGridOverlay();
  }

  onRowClicked(event: RowClickedEvent<CurrentQuoteListItem>): void {
    if (event.data) {
      this.openQuote(event.data);
    }
  }

  buildQuote(): void {
    void this.router.navigate(["/freight-command/spot-quote"]);
  }

  toggleTheme(): void {
    this.themeMode = this.isDarkTheme ? "light" : "dark";
    try {
      localStorage.setItem(this.themeStorageKey, this.themeMode);
    } catch (_error) {
      // Theme persistence is optional; the workbench remains usable without storage.
    }
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyFilters(): void {
    this.load(true);
  }

  resetFilters(): void {
    this.search = "";
    this.status = "";
    this.source = "";
    this.quoteMode = "";
    this.customerStatus = "";
    this.assessmentStatus = "";
    this.capacityStatus = "";
    this.priority = "";
    this.pickupFrom = "";
    this.pickupTo = "";
    this.onlyUnassigned = false;
    this.onlyBlockers = false;
    this.converted = "";
    this.sort = "activity_desc";
    this.load(true);
  }

  previousPage(): void {
    if (!this.canGoBack) {
      return;
    }
    this.offset = Math.max(0, this.offset - this.pageSize);
    this.load(false);
  }

  nextPage(): void {
    if (!this.hasMore) {
      return;
    }
    this.offset += this.pageSize;
    this.load(false);
  }

  pageSizeChanged(): void {
    this.load(true);
  }

  load(resetOffset = false): void {
    if (resetOffset) {
      this.offset = 0;
    }

    this.listSubscription?.unsubscribe();
    const requestId = ++this.listRequestSequence;
    this.loading = true;
    this.errorMessage = "";
    this.syncGridOverlay();

    this.listSubscription = this.quotesApi
      .listCurrent(this.buildListQuery())
      .subscribe({
        next: (response) => {
          if (requestId !== this.listRequestSequence) {
            return;
          }
          this.rows = Array.isArray(response.data) ? response.data : [];
          this.total = Number(response.meta?.total) || 0;
          this.hasMore = Boolean(response.meta?.has_more);
          this.loading = false;
          this.hasLoaded = true;
          this.syncGridOverlay();
          this.refreshSelectedSummary();
        },
        error: (error: HttpErrorResponse) => {
          if (requestId !== this.listRequestSequence) {
            return;
          }
          this.rows = [];
          this.total = 0;
          this.hasMore = false;
          this.loading = false;
          this.hasLoaded = true;
          this.errorMessage = this.errorText(
            error,
            "The quote queue could not be loaded.",
          );
          this.syncGridOverlay();
        },
      });
  }

  openQuote(quote: CurrentQuoteListItem): void {
    this.selectedSummary = quote;
    this.selectedDetail = null;
    this.detailError = "";
    this.pdfError = "";
    this.conversionError = "";
    this.conversionResult = null;
    this.fetchSelectedDetail();
  }

  closeDetail(): void {
    this.detailSubscription?.unsubscribe();
    this.detailRequestSequence += 1;
    this.selectedSummary = null;
    this.selectedDetail = null;
    this.detailLoading = false;
    this.detailError = "";
    this.pdfError = "";
    this.conversionError = "";
    this.conversionResult = null;
  }

  downloadSelectedPdf(): void {
    const quote = this.selectedDetail;
    if (!quote || this.pdfDownloading) {
      return;
    }
    const pdf = quote.rate_response['customer_quote_pdf'] as { filename?: string } | undefined;
    if (!pdf) {
      return;
    }
    this.pdfDownloading = true;
    this.pdfError = '';
    this.quotesApi.downloadCurrentPdf(quote.quote_uuid).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdf.filename || `${quote.display_quote_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.pdfDownloading = false;
      },
      error: () => {
        this.pdfDownloading = false;
        this.pdfError = 'Unable to download the saved quote PDF. Please retry.';
      }
    });
  }

  retryDetail(): void {
    if (this.selectedSummary) {
      this.fetchSelectedDetail();
    }
  }

  convertSelected(): void {
    const quote = this.selectedDetail || this.selectedSummary;
    if (
      !quote ||
      !quote.capabilities.can_convert_to_lading ||
      this.convertingQuoteUuid
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Convert ${quote.display_quote_number} into a lading/work order? This creates live operational records.`,
    );
    if (!confirmed) {
      return;
    }

    this.convertingQuoteUuid = quote.quote_uuid;
    this.conversionError = "";
    this.conversionResult = null;

    this.quotesApi
      .convertCurrent(
        quote.quote_uuid,
        quote.version,
        this.conversionIdempotencyKey(quote),
      )
      .subscribe({
        next: (response) => {
          this.convertingQuoteUuid = null;
          this.conversionResult = response.data;
          if (this.selectedSummary) {
            this.selectedSummary = {
              ...this.selectedSummary,
              status: response.data.quote_status,
              effective_status: response.data.quote_status,
              version: response.data.quote_version,
              converted_lading_uuid: response.data.lading_uuid,
              converted_manifest_uuid: response.data.manifest_uuid,
              capabilities: {
                ...this.selectedSummary.capabilities,
                can_convert_to_lading: false,
              },
            };
          }
          this.load(false);
          this.fetchSelectedDetail(true);
        },
        error: (error: HttpErrorResponse) => {
          this.convertingQuoteUuid = null;
          this.conversionError = this.errorText(
            error,
            `Unable to convert ${quote.display_quote_number} into a work order.`,
          );
        },
      });
  }

  openConvertedWorkOrder(): void {
    const ladingUuid =
      this.conversionResult?.lading_uuid ||
      this.selectedDetail?.converted_lading_uuid ||
      this.selectedSummary?.converted_lading_uuid;
    if (ladingUuid) {
      void this.router.navigate(["/work_order_details", ladingUuid]);
    }
  }

  label(value: unknown): string {
    const text = String(value ?? "").trim();
    if (!text) {
      return "Not set";
    }
    return text
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatMoney(
    cents: number | null | undefined,
    currency?: string | null,
  ): string {
    const amount = Number(cents);
    if (cents === null || cents === undefined || !Number.isFinite(amount)) {
      return "—";
    }
    const normalizedCurrency = /^[A-Z]{3}$/.test(
      String(currency || "").toUpperCase(),
    )
      ? String(currency).toUpperCase()
      : "";
    if (!normalizedCurrency) {
      return "—";
    }
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: normalizedCurrency,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch (_error) {
      return `${normalizedCurrency} ${(amount / 100).toFixed(2)}`;
    }
  }

  formatPercent(value: number | null | undefined): string {
    const percent = Number(value);
    return value === null || value === undefined || !Number.isFinite(percent)
      ? "—"
      : `${percent.toFixed(1)}%`;
  }

  formatCount(
    value: number | null | undefined,
    singular: string,
    plural = `${singular}s`,
  ): string {
    if (
      value === null ||
      value === undefined ||
      !Number.isFinite(Number(value))
    ) {
      return "Not set";
    }
    const count = Number(value);
    return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
  }

  conversionReadinessLabel(quote: CurrentQuoteListItem): string {
    if (quote.converted_lading_uuid || quote.effective_status === "converted") {
      return "Converted to a live work order";
    }
    return quote.capabilities.can_convert_to_lading
      ? "All release checks passed"
      : "Conversion is locked by the API readiness checks";
  }

  formatDate(value: string | null | undefined, includeTime = false): string {
    if (!value) {
      return "Not set";
    }
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const date = dateOnly
      ? new Date(
          Number(value.slice(0, 4)),
          Number(value.slice(5, 7)) - 1,
          Number(value.slice(8, 10)),
        )
      : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(
      "en-US",
      includeTime
        ? {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }
        : { month: "short", day: "numeric", year: "numeric" },
    ).format(date);
  }

  statusClass(value: string | null | undefined): string {
    const status = String(value || "not-set")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    return `qw-status qw-status--${status}`;
  }

  issueList(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .map((issue) => {
        if (typeof issue === "string") {
          return issue;
        }
        if (issue && typeof issue === "object") {
          const record = issue as Record<string, unknown>;
          const message =
            record["message"] ||
            record["detail"] ||
            record["label"] ||
            record["code"];
          return message ? String(message) : JSON.stringify(record);
        }
        return String(issue ?? "");
      })
      .filter(Boolean);
  }

  customerCharges(detail: CurrentQuoteDetail): QuoteCustomerCharge[] {
    return detail.workbench?.customer_charges || [];
  }

  carrierCosts(detail: CurrentQuoteDetail): QuoteCarrierCost[] {
    return detail.workbench?.carrier_costs || [];
  }

  carrierOptions(detail: CurrentQuoteDetail): QuoteCarrierOption[] {
    return detail.workbench?.carrier_options || [];
  }

  rateObservations(detail: CurrentQuoteDetail): QuoteRateObservation[] {
    return detail.workbench?.rate_observations || [];
  }

  historicalMatches(detail: CurrentQuoteDetail): QuoteHistoricalMatch[] {
    return detail.workbench?.historical_matches || [];
  }

  quoteEvents(detail: CurrentQuoteDetail): QuoteEvent[] {
    return detail.workbench?.events || [];
  }

  acceptances(detail: CurrentQuoteDetail): QuoteAcceptance[] {
    return detail.workbench?.acceptances || [];
  }

  lineItems(header: QuoteCustomerCharge | QuoteCarrierCost): QuoteLineItem[] {
    return Array.isArray(header.line_items) ? header.line_items : [];
  }

  trackByUuid(_index: number, item: any): string {
    const key = Object.keys(item).find((field) => field.endsWith("_uuid"));
    return key ? String(item[key]) : String(_index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private buildListQuery(): QuoteListQuery {
    return {
      limit: this.pageSize,
      offset: this.offset,
      sort: this.sort,
      ...(this.search.trim() ? { search: this.search.trim() } : {}),
      ...(this.status ? { status: this.status } : {}),
      ...(this.source ? { source: this.source } : {}),
      ...(this.quoteMode ? { quote_mode: this.quoteMode } : {}),
      ...(this.customerStatus ? { customer_status: this.customerStatus } : {}),
      ...(this.assessmentStatus
        ? { assessment_status: this.assessmentStatus }
        : {}),
      ...(this.capacityStatus ? { capacity_status: this.capacityStatus } : {}),
      ...(this.priority !== "" ? { priority: Number(this.priority) } : {}),
      ...(this.pickupFrom ? { pickup_from: this.pickupFrom } : {}),
      ...(this.pickupTo ? { pickup_to: this.pickupTo } : {}),
      ...(this.onlyUnassigned ? { unassigned: true } : {}),
      ...(this.onlyBlockers ? { has_blockers: true } : {}),
      ...(this.converted ? { converted: this.converted === "true" } : {}),
    };
  }

  private fetchSelectedDetail(preserveConversionNotice = false): void {
    const quoteUuid = this.selectedSummary?.quote_uuid;
    if (!quoteUuid) {
      return;
    }

    this.detailSubscription?.unsubscribe();
    const requestId = ++this.detailRequestSequence;
    this.detailLoading = true;
    this.detailError = "";
    this.pdfError = "";
    if (!preserveConversionNotice) {
      this.conversionError = "";
    }

    this.detailSubscription = this.quotesApi.getCurrent(quoteUuid).subscribe({
      next: (response) => {
        if (
          requestId !== this.detailRequestSequence ||
          this.selectedSummary?.quote_uuid !== quoteUuid
        ) {
          return;
        }
        this.selectedDetail = response.data;
        this.selectedSummary = response.data;
        this.detailLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        if (
          requestId !== this.detailRequestSequence ||
          this.selectedSummary?.quote_uuid !== quoteUuid
        ) {
          return;
        }
        this.selectedDetail = null;
        this.detailLoading = false;
        this.detailError = this.errorText(
          error,
          "Quote detail could not be loaded.",
        );
      },
    });
  }

  private refreshSelectedSummary(): void {
    if (!this.selectedSummary) {
      return;
    }
    const updated = this.rows.find(
      (quote) => quote.quote_uuid === this.selectedSummary?.quote_uuid,
    );
    if (updated) {
      this.selectedSummary = this.selectedDetail || updated;
    }
  }

  private syncGridOverlay(): void {
    if (!this.gridApi) {
      return;
    }
    if (this.loading) {
      this.gridApi.showLoadingOverlay();
    } else if (!this.rows.length) {
      this.gridApi.showNoRowsOverlay();
    } else {
      this.gridApi.hideOverlay();
    }
  }

  private renderQuoteCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    return `<div class="qw-grid-stack"><strong>${this.escapeHtml(quote.display_quote_number)}</strong><span>${this.escapeHtml(this.label(quote.source))} · ${this.escapeHtml(this.label(quote.quote_mode))} · P${Number(quote.quote_priority) || 0}</span><em class="${this.statusClass(quote.effective_status)}">${this.escapeHtml(this.label(quote.effective_status))}</em></div>`;
  }

  private renderCustomerCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    return `<div class="qw-grid-stack"><strong>${this.escapeHtml(quote.customer_display_name || "Unnamed customer")}</strong><span>${this.escapeHtml(this.label(quote.customer_status))}</span></div>`;
  }

  private renderLaneCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    return `<div class="qw-grid-lane"><strong>${this.escapeHtml(quote.origin_display || "Origin pending")}</strong><i class="fa-solid fa-arrow-right-long"></i><strong>${this.escapeHtml(quote.destination_display || "Destination pending")}</strong></div>`;
  }

  private renderFreightCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    const freight = quote.freight;
    const details = [
      freight.equipment_type,
      freight.freight_load_type ? this.label(freight.freight_load_type) : "",
      freight.rated_miles !== null && freight.rated_miles !== undefined
        ? `${Number(freight.rated_miles).toLocaleString()} mi`
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
    return `<div class="qw-grid-stack"><strong>${this.escapeHtml(this.formatDate(freight.requested_pickup_date))}</strong><span>${this.escapeHtml(details || "Freight details pending")}</span></div>`;
  }

  private renderMoneyCell(
    cents?: number | null,
    currency?: string | null,
    caption = "",
  ): string {
    return `<div class="qw-grid-money"><strong>${this.escapeHtml(this.formatMoney(cents, currency))}</strong><span>${this.escapeHtml(caption)}</span></div>`;
  }

  private renderMarginCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    if (quote.pricing.currency_mismatch) {
      return '<div class="qw-grid-money qw-grid-money--warning"><strong>Currency mismatch</strong><span>Margin unavailable</span></div>';
    }
    const margin = Number(quote.pricing.margin_amount_cents);
    const tone =
      Number.isFinite(margin) && margin < 0 ? "negative" : "positive";
    return `<div class="qw-grid-money qw-grid-money--${tone}"><strong>${this.escapeHtml(this.formatMoney(quote.pricing.margin_amount_cents, quote.pricing.currency))}</strong><span>${this.escapeHtml(this.formatPercent(quote.pricing.margin_percent))}</span></div>`;
  }

  private renderCarrierCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    const carrier = quote.selected_carrier;
    return `<div class="qw-grid-stack"><strong>${this.escapeHtml(carrier?.carrier_name || "Carrier not selected")}</strong><span>${this.escapeHtml(carrier ? this.label(carrier.option_status || carrier.cost_status) : this.label(quote.capacity_status))}</span></div>`;
  }

  private renderReadinessCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    return `<div class="qw-grid-badges"><span class="${this.statusClass(quote.assessment_status)}">${this.escapeHtml(this.label(quote.assessment_status))}</span><span class="${this.statusClass(quote.capacity_status)}">${this.escapeHtml(this.label(quote.capacity_status))}</span>${quote.pricing.blocker_count > 0 ? `<span class="qw-status qw-status--blocked">${quote.pricing.blocker_count} blocker${quote.pricing.blocker_count === 1 ? "" : "s"}</span>` : ""}</div>`;
  }

  private renderActivityCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    return `<div class="qw-grid-stack"><strong>${this.escapeHtml(quote.assignment?.username || "Unassigned")}</strong><span>${this.escapeHtml(this.formatDate(quote.last_activity_at, true))}</span></div>`;
  }

  private renderExpiryCell(quote?: CurrentQuoteListItem): string {
    if (!quote) {
      return "";
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(quote.expiry_date || "");
    const expiry = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(quote.expiry_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const days = Number.isNaN(expiry.getTime())
      ? null
      : Math.round((expiry.getTime() - today.getTime()) / 86400000);
    const caption =
      quote.effective_status === "expired" || (days !== null && days < 0)
        ? "Expired"
        : days === null
          ? "Validity pending"
          : days === 0
            ? "Expires today"
            : `${days} day${days === 1 ? "" : "s"} remaining`;
    return `<div class="qw-grid-stack"><strong>${this.escapeHtml(this.formatDate(quote.expiry_date))}</strong><span>${this.escapeHtml(caption)}</span></div>`;
  }

  private escapeHtml(value: unknown): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private conversionIdempotencyKey(quote: CurrentQuoteListItem): string {
    return `quotes-convert:${quote.quote_uuid}:v${quote.version}`;
  }

  private storedTheme(): ThemeMode {
    try {
      return localStorage.getItem(this.themeStorageKey) === "light"
        ? "light"
        : "dark";
    } catch (_error) {
      return "dark";
    }
  }

  private errorText(error: HttpErrorResponse, fallback: string): string {
    const payload = error.error as
      | { message?: string; error?: string; code?: string }
      | string
      | null;
    if (typeof payload === "string" && payload.trim()) {
      return payload;
    }
    if (payload && typeof payload === "object") {
      return payload.message || payload.error || fallback;
    }
    return error.message || fallback;
  }
}
